/* eslint-disable @next/next/no-img-element */
/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Search, MapPin, ArrowLeft, ArrowRight } from "lucide-react";
import Link from "next/link";

/* =====================
   HELPERS
===================== */
const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");

/* =====================
   CONFIG
===================== */
const PAGE_SIZE = 6;

/* =====================
   PAGE
===================== */
export default function StorefrontPage() {
  const [agents, setAgents] = useState<any[]>([]);
  const [filteredAgents, setFilteredAgents] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState("Newest");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  /* =====================
     FETCH AGENTS
  ====================== */
  useEffect(() => {
    async function loadAgents() {
      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/api/v1/zeppier-agent/storefront/agents`,
          { cache: "no-store" },
        );
        const json = await res.json();
        const list = json?.data || [];
        setAgents(list);
        setFilteredAgents(list);
      } catch (err) {
        console.error("Agent fetch error:", err);
      } finally {
        setLoading(false);
      }
    }

    loadAgents();
  }, []);

  /* =====================
     CATEGORY LIST
  ====================== */
  const categories = useMemo(() => {
    const set = new Set(agents.map((a) => a.category || "Real Estate"));
    return ["All", ...Array.from(set)];
  }, [agents]);

  /* =====================
     FILTER + SORT
  ====================== */
  useEffect(() => {
    let data = [...agents];

    if (category !== "All") {
      data = data.filter((a) => (a.category || "Real Estate") === category);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter((a) =>
        `${a.data?.firstname} ${a.data?.lastname}`.toLowerCase().includes(q),
      );
    }

    if (sort === "Newest") {
      data.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
    }

    if (sort === "Oldest") {
      data.sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
    }

    if (sort === "Name A–Z") {
      data.sort((a, b) =>
        `${a.data?.firstname}`.localeCompare(`${b.data?.firstname}`),
      );
    }

    setPage(1);
    setFilteredAgents(data);
  }, [agents, searchQuery, category, sort]);

  /* =====================
     PAGINATION
  ====================== */
  const totalPages = Math.ceil(filteredAgents.length / PAGE_SIZE);

  const paginatedAgents = filteredAgents.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  /* =====================
     LOADING
  ====================== */
  if (loading) {
    return (
      <div className="flex justify-center items-center h-[70vh]">
        <div className="w-12 h-12 border-4 border-gray-300 border-t-[#4177BC] rounded-full animate-spin" />
      </div>
    );
  }

  /* =====================
     RENDER
  ====================== */
  return (
    <div className="min-h-screen bg-white px-4 sm:px-10 lg:px-32 py-10">
      {/* =====================
          TOP BAR
      ====================== */}
      {/* =====================
    TOP CONTROL BAR
===================== */}
      <div className="mb-16">
        <div
          className="
    bg-white/80 backdrop-blur-xl
    border border-gray-200
    rounded-3xl
    px-6 py-5
    shadow-md
    flex flex-col lg:flex-row
    gap-5
    items-center
    justify-between
  "
        >
          {/* LEFT: Back + Title */}
          <div className="flex items-center gap-4 w-full lg:w-auto">
            {/* Back to Home */}
            <Link
              href="/"
              className="
    flex items-center gap-2
    px-4 py-2
    rounded-xl
    border border-gray-300
    text-sm font-medium
    text-gray-700
    hover:bg-gray-100
    transition
  "
            >
              <ArrowLeft className="w-4 h-4" />
              Home
            </Link>

            {/* Page title */}
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-[#101828]">
                Local Professionals
              </h1>
              <p className="text-sm text-gray-500">
                Find trusted experts near you
              </p>
            </div>
          </div>

          {/* RIGHT: Controls */}
          <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto">
            {/* Search */}
            <div
              className="
        flex items-center
        bg-white
        border border-gray-300
        rounded-xl
        px-4 py-2.5
        w-full sm:w-[260px]
      "
            >
              <Search className="w-4 h-4 text-gray-400 mr-2" />
              <input
                placeholder="Search agents…"
                className="w-full outline-none bg-transparent text-sm text-gray-700"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Category */}
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="
          bg-white
          border border-gray-300
          rounded-xl
          px-4 py-2.5
          text-sm text-gray-700
        "
            >
              {categories.map((cat) => (
                <option key={cat}>{cat}</option>
              ))}
            </select>

            {/* Sort */}
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="
          bg-white
          border border-gray-300
          rounded-xl
          px-4 py-2.5
          text-sm text-gray-700
        "
            >
              <option>Newest</option>
              <option>Oldest</option>
              <option>Name A–Z</option>
            </select>
          </div>
        </div>
      </div>

      {/* =====================
          GRID
      ====================== */}
      {paginatedAgents.length === 0 ? (
        <p className="text-center text-gray-500 py-20">No agents found.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {paginatedAgents.map((agent) => {
            const slug = slugify(
              `${agent.data?.firstname}-${agent.data?.lastname}`,
            );

            return (
              <Link
                key={agent._id}
                href={`/storefront/${slug}`}
                className="group relative h-[520px] rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition"
              >
                {/* Image */}
                <img
                  src={
                   agent?.data?.headshot || "/storefront/agent-placeholder.png"
                  }
                  alt=""
                  className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700"
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-linear-to-t from-black/90 via-black/50 to-transparent" />

                {/* Content */}
                <div className="absolute bottom-0 p-7 text-white z-10">
                  <span className="inline-block mb-3 px-3 py-1 text-xs rounded-full bg-[#22C55E]">
                    Available
                  </span>

                  <h3 className="text-2xl font-semibold">
                    {agent.data?.firstname} {agent.data?.lastname}
                  </h3>

                  <p className="flex items-center text-sm text-gray-200 mt-1">
                    <MapPin className="w-4 h-4 mr-1 text-[#EB9D2C]" />
                    {agent.data?.city}, {agent.data?.state}
                  </p>

                  <p className="text-sm text-gray-300 mt-1">
                    {agent.data?.brokerageName}
                  </p>

                  {/* ABOUT */}
                  <p className="mt-3 text-sm text-gray-200 line-clamp-3 max-w-md">
                    {agent.data?.about}
                  </p>

                  <div
                    className="
    mt-5 inline-flex items-center gap-2
    bg-white text-black
    px-5 py-2
    rounded-full
    text-sm font-medium
    transition
    group-hover:bg-[#4177BC]
    group-hover:text-white
  "
                  >
                    View Profile
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* =====================
          PAGINATION
      ====================== */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-6 mt-16">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="p-2 rounded-full border disabled:opacity-40"
          >
            <ArrowLeft />
          </button>

          <span className="text-gray-600">
            Page {page} of {totalPages}
          </span>

          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="p-2 rounded-full border disabled:opacity-40"
          >
            <ArrowRight />
          </button>
        </div>
      )}
    </div>
  );
}
